import { GraphQLError } from 'graphql'
import { DateTimeResolver, JSONResolver, PositiveIntResolver } from 'graphql-scalars'
import { PermissionEnum } from 'scalis-db-v2'
import SchemaBuilder from '@pothos/core'
import PrismaPlugin from '@pothos/plugin-prisma'
import type PrismaTypes from '@pothos/plugin-prisma/generated'
import ScopeAuthPlugin from '@pothos/plugin-scope-auth'
import ValidationPlugin from '@pothos/plugin-validation'
import WithInputPlugin from '@pothos/plugin-with-input'
import { AuthenticatedUser } from '~/types/auth/TokenPayload'
import { prismaClientScalisV2 } from '~/utils/prismaClientScalisV2'
import { OrganizationRole } from '~/v2/enums'

export type AuthScopes = {
  readonly public: boolean
  readonly isLogged: boolean
  readonly isCompanyUser: boolean
} & {
  [key in PermissionEnum | keyof typeof OrganizationRole]: boolean
}

export type Context = {
  currentUser: AuthenticatedUser
  subdomain: string | null
  companyUserId?: number
  request: Request
  params: unknown
}

export const schemaBuilder = new SchemaBuilder<{
  Objects: {
    EventData: 'EventData'
  }
  Scalars: {
    JSON: {
      Input: unknown
      Output: unknown
    }
    DateTime: {
      Input: Date
      Output: Date
    }
    Int: {
      Input: number
      Output: number
    }
    String: {
      Input: string
      Output: string
    }
  }
  PrismaTypes: PrismaTypes
  AuthScopes: AuthScopes
  Context: Context
}>({
  plugins: [ScopeAuthPlugin, PrismaPlugin, WithInputPlugin, ValidationPlugin],
  prisma: {
    client: prismaClientScalisV2,
    onUnusedQuery: process.env.NODE_ENV === 'production' ? null : 'warn',
  },
  scopeAuth: {
    authorizeOnSubscribe: true,
    unauthorizedError: (_, __, ___, result) =>
      new GraphQLError('Unauthorized', {
        extensions: {
          code: 'UNAUTHORIZED',
          message: `Missing required permission`,
          text: result,
        },
      }),

    /**
     * Check the Permissions API Mapping spreadsheet for the list of permissions
     * https://docs.google.com/spreadsheets/d/1A-mXDCZOJmL2VLw1sEx0ekK6rf8G9IYeeC6XM9xlUkE/edit?usp=sharing
     *
     * Please note: those permissions only cover the Org level permissions.
     * For the job level permissions, we need to implement it in the resolver level
     */
    authScopes: async (context) => {
      const { currentUser } = context

      const isSuperAdmin = !!currentUser?.workspace.currentCompany?.roles?.includes(OrganizationRole.SUPER_ADMIN)
      const isAdmin = !!currentUser?.workspace.currentCompany?.roles?.includes(OrganizationRole.ADMIN)

      const defaultPermissions = [...Object.values(PermissionEnum), ...Object.values(OrganizationRole)].reduce(
        (acc, permission) => {
          acc[permission] = isSuperAdmin || isAdmin
          return acc
        },
        {} as Record<PermissionEnum | keyof typeof OrganizationRole, boolean>,
      )

      if (!isSuperAdmin && isAdmin) {
        defaultPermissions[PermissionEnum.DEMOGRAPHIC_INFORMATION__VIEW] = false
        defaultPermissions[PermissionEnum.COMPANY_SETTINGS__EDIT] = false
      }

      const userPermissionsAndRoles = [
        ...(currentUser?.workspace?.currentCompany?.permissions ?? []),
        ...(currentUser?.workspace?.currentCompany?.roles ?? []),
      ].reduce(
        (acc, permission) => {
          acc[permission] = true
          return acc
        },
        {} as Record<PermissionEnum | keyof typeof OrganizationRole, boolean>,
      )

      return {
        ...defaultPermissions,
        ...userPermissionsAndRoles,
        public: true,
        isLogged: !!currentUser?.accessToken,
        isCompanyUser: !!currentUser?.workspace.companyUser,
      }
    },
  },
})

schemaBuilder.addScalarType('JSON', JSONResolver)
schemaBuilder.addScalarType('DateTime', DateTimeResolver, {})
schemaBuilder.addScalarType('Int', PositiveIntResolver, {})

schemaBuilder.queryType({})
schemaBuilder.mutationType({})
